import { defineComponent as _defineComponent } from 'vue'
import { onMounted, PropType, ref } from 'vue';
import { defineMessages } from '@/lib/i18n';
import { formatToDate, formatToDateTime } from '@/utils/datetime';
import {
  GetCompanyPeriodicExportSetting,
  GetCompanyPeriodicExportSettingDetail,
  GetSendableCompanyUser,
} from '@/interfaces/api/general/company';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import {
  readAssertedCompanyId,
  readUserLocale,
  readAssertedUserProfile,
} from '@/store/main/getters';
import { store } from '@/store/index';
import PeriodicExportFormDialog, {
  ExportSettingForm,
} from '@/views/pages/companyPeriodicExport/PeriodicExportFormDialog.vue';
import PeriodicExportDeleteDialog from '@/views/pages/companyPeriodicExport/PeriodicExportDeleteDialog.vue';
import { useSdsesExport } from '@/composables/useSdsesExport';
import { AxiosResponse } from 'axios';
import {
  dispatchGetCompanyPeriodicExportSettingDetail,
  dispatchGetSendableCompanyUsers,
} from '@/store/main/actionsMain';
import { datePatterns, dayOfWeeks } from './constants';
import { commitAddNotification } from '@/store/main/mutations';
import {
  EXPORT_FILE_FORMAT_ALL_CHAPTERS,
  EXPORT_FILE_FORMAT_CREATE_SIMPLE,
  EXPORT_FILE_FORMAT_CSV,
  EXPORT_FILE_FORMAT_EXCEL,
  EXPORT_FORMAT_CSV_CHEMICAL,
  EXPORT_FORMAT_CSV_PRODUCT,
  EXPORT_FORMAT_CSV_SDS,
} from '@/lib/constant';


export default /*#__PURE__*/_defineComponent({
  __name: 'PeriodicExportTable',
  props: {
  exportSettings: {
    type: Array as PropType<GetCompanyPeriodicExportSetting[]>,
    required: true,
    default: () => [],
  },
},
  emits: ["updateExportSetting", "deleleExportSetting"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'updateExportSetting', form: ExportSettingForm): void;
  (
    e: 'deleleExportSetting',
    exportSetting: GetCompanyPeriodicExportSetting | undefined
  ): void;
}), expose: any, slots: any, attrs: any }) {

const m = defineMessages({
  disabledMessage:
    '受信者に変更があったため、この定期エクスポートは無効化されています。',
});




const $t = i18n.t.bind(i18n);
const sendableUsers = ref<GetSendableCompanyUser[]>([]);
const editTarget = ref<GetCompanyPeriodicExportSettingDetail>();
const deleteTarget = ref<GetCompanyPeriodicExportSetting>();
const highlightRow = ref(0);
const isOpenUpdateModal = ref(false);
const isOpenDeleteModal = ref(false);
const companyId = readAssertedCompanyId(store);
const currentUser = readAssertedUserProfile(store);
const { exportOperations, fetchCompanyExportOperations } = useSdsesExport();

const updateExportSetting = (form: ExportSettingForm) => {
  emits('updateExportSetting', form);
};

// エクスポート種別の文字列取得
const getExportTypeString = (exportType: string): string => {
  switch (exportType) {
    case 'normal':
      return $t(sharedMessages.default).toString();
    case 'custom':
      return $t(sharedMessages.custom).toString();
    case 'risk_assessment':
      return $t(sharedMessages.riskAssessment).toString();
    default:
      return '';
  }
};
// ファイル形式の文字列取得（カスタムの場合は現状変換なし）
// リスク評価はファイル形式の選択を持たない（固定値"csv"が入るが標準CSVエクスポートとは
// 無関係なため表示しない）
const getFileFormatString = (
  exportType: string,
  fileFormat: string
): string => {
  if (exportType === 'risk_assessment') {
    return '-';
  }
  switch (fileFormat) {
    case EXPORT_FILE_FORMAT_EXCEL:
      return $t(sharedMessages.exportFileFormatExcel).toString();
    case EXPORT_FILE_FORMAT_CSV:
      return $t(sharedMessages.exportFileFormatCsv).toString();
    case EXPORT_FILE_FORMAT_CREATE_SIMPLE:
      return $t(sharedMessages.exportFileFormatCreateSimple).toString();
    case EXPORT_FILE_FORMAT_ALL_CHAPTERS:
      return $t(sharedMessages.exportFileFormatAllChapters).toString();
    default:
      return fileFormat;
  }
};
// エクスポート形式の文字列取得（カスタムの場合は現状変換なし）
// リスク評価はエクスポート形式の選択を持たない
const getExportFormatString = (
  exportType: string,
  fileFormat: string,
  exportFormat: string | undefined
): string => {
  if (exportType === 'risk_assessment') {
    return '-';
  }
  if (fileFormat === EXPORT_FILE_FORMAT_CSV) {
    switch (exportFormat) {
      case EXPORT_FORMAT_CSV_SDS:
        return $t(sharedMessages.exportFormatCsvSds).toString();
      case EXPORT_FORMAT_CSV_PRODUCT:
        return $t(sharedMessages.exportFormatCsvProduct).toString();
      case EXPORT_FORMAT_CSV_CHEMICAL:
        return $t(sharedMessages.exportFormatCsvChemical).toString();
      default:
        return '';
    }
  } else {
    return exportFormat ? exportFormat : '';
  }
};
// エクスポート文字コードの文字列取得
const getExportCharacterCodeString = (
  exportCharacterCode: string | undefined
): string => {
  switch (exportCharacterCode) {
    case 'shift-jis':
      return 'Shift-JIS';
    case 'utf-8':
      return 'UTF-8';
    default:
      return '';
  }
};

const weekdayMap = new Map<string, string>();
const weekdayStrings = $t(sharedMessages.weekdayFullSundayToSaturday)
  .toString()
  .split(',');
dayOfWeeks.forEach((day, index) => {
  weekdayMap.set(day, weekdayStrings[index]);
});
const getWeekdayFullString = (exportDayOfWeek: string): string => {
  return weekdayMap.get(exportDayOfWeek) || '';
};

const datePatternMap = new Map<string, string>();
const datePatternStrings = $t(sharedMessages.exportDatePatternList)
  .toString()
  .split(',');
datePatterns.forEach((day, index) => {
  datePatternMap.set(day, datePatternStrings[index]);
});
const getDatePatternString = (exportDatePattern: string): string => {
  return datePatternMap.get(exportDatePattern) || '';
};

// 定期エクスポート日時の文字列取得
const getExportDateString = (
  exportTime: string,
  exportFrequency: string,
  exportDatePattern: string | undefined,
  exportDayOfWeek: string | undefined
): string => {
  const locale = readUserLocale(store);

  if (locale === 'en-US') {
    switch (exportFrequency) {
      case 'daily':
        return `Every ${exportTime}`;
      case 'weekly':
        return exportDayOfWeek
          ? `Every ${getWeekdayFullString(exportDayOfWeek)} ${exportTime}`
          : '';
      case 'monthly':
        return exportDatePattern
          ? `Every ${getDatePatternString(exportDatePattern)} ${exportTime}`
          : '';
      default:
        return '';
    }
  } else {
    switch (exportFrequency) {
      case 'daily':
        return `毎日 ${exportTime}`;
      case 'weekly':
        return exportDayOfWeek
          ? `毎週${getWeekdayFullString(exportDayOfWeek)} ${exportTime}`
          : '';
      case 'monthly':
        return exportDatePattern
          ? `毎月${getDatePatternString(exportDatePattern)} ${exportTime}`
          : '';
      default:
        return '';
    }
  }
};

const deleleExportSetting = async (
  e: GetCompanyPeriodicExportSetting | undefined
) => {
  emits('deleleExportSetting', e);
  deleteTarget.value = undefined;
};

const showUpdateModal = async (
  exportSetting: GetCompanyPeriodicExportSetting
) => {
  try {
    const response: AxiosResponse<GetCompanyPeriodicExportSettingDetail> =
      await dispatchGetCompanyPeriodicExportSettingDetail(store, {
        companyId,
        settingId: exportSetting.id,
      });
    if (response?.data) {
      editTarget.value = response.data;
    }
    isOpenUpdateModal.value = true;
  } catch (e) {
    console.error(e);
    commitAddNotification(store, {
      messageKey: sharedMessages.genericError,
      color: 'error',
    });
  }
};

const closeUpdateModal = () => {
  isOpenUpdateModal.value = false;
  editTarget.value = undefined;
};

const showDeleteDialog = (exportSetting: GetCompanyPeriodicExportSetting) => {
  isOpenDeleteModal.value = true;
  deleteTarget.value = { ...exportSetting };
};

const fetchSendableCompanyUsers = async (companyId: string) => {
  const response: AxiosResponse<GetSendableCompanyUser[]> =
    await dispatchGetSendableCompanyUsers(store, {
      companyId,
    });
  if (response?.data) {
    sendableUsers.value = response.data;
  }
};

onMounted(async () => {
  await Promise.all([
    fetchCompanyExportOperations(companyId),
    fetchSendableCompanyUsers(companyId),
  ]);
});

return { __sfc: true,m, emits, $t, sendableUsers, editTarget, deleteTarget, highlightRow, isOpenUpdateModal, isOpenDeleteModal, companyId, currentUser, exportOperations, fetchCompanyExportOperations, updateExportSetting, getExportTypeString, getFileFormatString, getExportFormatString, getExportCharacterCodeString, weekdayMap, weekdayStrings, getWeekdayFullString, datePatternMap, datePatternStrings, getDatePatternString, getExportDateString, deleleExportSetting, showUpdateModal, closeUpdateModal, showDeleteDialog, fetchSendableCompanyUsers, formatToDate, formatToDateTime, sharedMessages, PeriodicExportFormDialog, PeriodicExportDeleteDialog }
}

})